import { createContext } from 'react'
import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormGenerator } from '@microservices/wiskey-react-components'
import { Settings as SettingsIcon } from '@mui/icons-material'
import { Box, Button, CircularProgress, Grid, Typography } from '@mui/material'

import { MapSettingsDialog } from '@pages/MapCreateOrEdit/components/MapSettingsDialog'
import { useMapCreateOrEdit } from '@pages/MapCreateOrEdit/hooks'
import { PageContextType } from '@pages/MapCreateOrEdit/types'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { EditConfigMenu } from '@components/ConfigEditMenu'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'
import { GeneralButtons } from '@components/GeneralButtons'
import { SwitchTable } from '@components/SwitchTable'
import { Tab, TabPanel, Tabs } from '@components/Tabs'

import { EditConfigProvider } from '@providers/EditConfigProvider'

import { ENTITY_CONFIG_TYPE, MAP_TABS, MODAL_TYPE, MODEL_TYPE } from '@constants'

export const PageContext = createContext<PageContextType>({} as PageContextType)

export const MapCreateOrEdit = () => {
  const { t } = useTranslation()

  const { state, handlers, inputs } = useMapCreateOrEdit()

  const {
    methods,
    isCreating,
    mapSettingsDialogData,
    showAddPointGroupDialog,
    addPointGroupDialogData,
    watchPointGroups,
    watchId,
    isDirty,
    isLoadingConfig,
  } = state

  const {
    handleSaveMap,
    handleCancelMap,
    handleDeletePointGroup,
    handleOpenMapSettingsDialog,
    handleCloseMapSettingsDialog,
    handleUpdateMapSettings,
    handleShowAddPointGroupDialog,
    handleCloseAddPointGroupDialog,
    handleUpdatePointGroups,
  } = handlers

  const { generalInputs } = inputs

  const contextValue: PageContextType = {
    addPointGroupDialogData,
    handleCloseAddPointGroupDialog,
    handleUpdatePointGroups,
    mapSettingsDialogData,
    handleCloseMapSettingsDialog,
    handleUpdateMapSettings,
  }

  return (
    <PageContext.Provider value={contextValue}>
      <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.MAPS}>
        <PageContentLayout sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                flex: 1,
                overflowY: 'auto',
                p: 1,
              }}
            >
              <EditConfigScriptDialog />
              <Grid container alignItems={'center'} justifyContent={'space-between'} py={0.5}>
                <Typography mb={1} variant='h5'>
                  {t(`mapCreateOrEdit.header`)}
                </Typography>
                {watchId && <EditConfigMenu configId={watchId} />}
              </Grid>
              {isLoadingConfig ? (
                <Box
                  sx={{ flex: 1, alignItems: 'center', display: 'flex', justifyContent: 'center' }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <FormProvider {...methods}>
                  <MapSettingsDialog />
                  <Grid container display='flex' pb={2} pt={2} spacing={2}>
                    <Grid item xs={12}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          mb: 1,
                        }}
                      >
                        <Typography py={0.5} variant='h5'>
                          {t('mapCreateOrEdit.general')}
                        </Typography>
                        <Button
                          startIcon={<SettingsIcon />}
                          variant='outlined'
                          onClick={handleOpenMapSettingsDialog}
                        >
                          {t('mapCreateOrEdit.mapSettingsButton')}
                        </Button>
                      </Box>
                      <FormGenerator inputs={generalInputs} />
                    </Grid>
                  </Grid>

                  <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={MAP_TABS.POINT_GROUPS}>
                      <Tab
                        label={t('mapCreateOrEdit.tabs.pointGroups')}
                        value={MAP_TABS.POINT_GROUPS}
                      />
                    </Tabs>
                  </Box>

                  <TabPanel index={MAP_TABS.POINT_GROUPS} value={MAP_TABS.POINT_GROUPS}>
                    <SwitchTable
                      disablePagination
                      disableSort
                      showActionsColumn
                      btnText={t('mapCreateOrEdit.pointGroups.addPointGroup')}
                      rows={watchPointGroups}
                      showDialog={showAddPointGroupDialog}
                      type={MODEL_TYPE.MAP_POINT_GROUP}
                      columns={[
                        {
                          accessorKey: 'title',
                          header: t('mapCreateOrEdit.pointGroups.title'),
                          meta: { flex: 1 },
                        },
                        /* {
                          accessorKey: 'objectCode',
                          header: t('mapCreateOrEdit.pointGroups.objectCode'),
                          meta: { flex: 1 },
                        },*/
                      ]}
                      containerSx={{
                        '.MuiTableBody-root > div': {
                          height: '400px !important',
                        },
                        pr: 0,
                      }}
                      onDelete={rowId => handleDeletePointGroup(rowId as string)}
                      onShowDialog={handleShowAddPointGroupDialog}
                      onEdit={params =>
                        handleShowAddPointGroupDialog(MODAL_TYPE.EDIT, params.row.original.id!)
                      }
                    />
                  </TabPanel>
                </FormProvider>
              )}
            </Box>
          </Box>
        </PageContentLayout>
        <Box
          sx={{
            position: 'sticky',
            bottom: 0,
            width: '100%',
            flexShrink: 0,
          }}
        >
          <Grid
            container
            direction='column'
            sx={{
              background: 'white',
              borderTop: '1px solid',
              borderColor: 'grey.300',
              boxShadow: '0 -1px 3px rgba(0,0,0,0.05)',
            }}
          >
            <Grid item>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  pt: 1,
                  pb: 1,
                  pr: 1,
                }}
              >
                <GeneralButtons
                  cancelText={t('mapCreateOrEdit.cancelButton')}
                  isLoading={isCreating || isLoadingConfig}
                  saveDisabled={!isDirty || isCreating}
                  saveText={t('mapCreateOrEdit.saveButton')}
                  onCancel={handleCancelMap}
                  onSave={handleSaveMap}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>
      </EditConfigProvider>
    </PageContext.Provider>
  )
}
